import { useGetGeographicPinInteractionHandler } from 'features/siteMap/mapInteraction/UseGetGeographicPinInteractionHandler'
import { FC, useMemo } from 'react'
import {
  GeckoGeographicPin,
  SiteFeatureCompositeKind,
  SiteFeaturePin,
} from 'types'

import { GeographicPinFeature } from './features/GeographicPinFeature'

interface GeographicPinsProps {
  pins: readonly GeckoGeographicPin[]
}

const castGeckoPinToFeaturePin = (pin: GeckoGeographicPin): SiteFeaturePin => {
  const geometry = pin.data.geo_json.geometry
  if (!geometry || geometry.type !== 'Point') {
    throw new Error(
      `Expected GeoJSON geometry type 'Point' but received ${geometry?.type} for pin ${pin.id}`
    )
  }

  const coordinates = geometry.coordinates

  return {
    kind: SiteFeatureCompositeKind.Pin,
    geometry: {
      radius: pin.data.geo_json.properties?.radius ?? 10,
    },
    coordinate: {
      lat: coordinates[1],
      lon: coordinates[0],
      height: coordinates[2] ?? 1800,
    },
    rotation: [0, 0, 0],
    name: pin.data.geo_json.properties?.name ?? '',
    id: pin.id,
    unit_id: null,
    area_id: null,
    color: pin.data.geo_json.properties?.color ?? 'red',
  }
}

interface PinWithAttachmentsProps {
  pin: GeckoGeographicPin
}

const PinWithAttachments: FC<PinWithAttachmentsProps> = ({ pin }) => {
  const { handleClick, hoverImage } = useGetGeographicPinInteractionHandler(pin)
  const featurePin = castGeckoPinToFeaturePin(pin)

  return (
    <GeographicPinFeature
      pin={featurePin}
      onClick={handleClick}
      image={hoverImage}
    />
  )
}

export const GeographicPins: FC<GeographicPinsProps> = ({ pins }) => {
  const pinsComponents = useMemo(() => {
    return pins.map((pin) => <PinWithAttachments key={pin.id} pin={pin} />)
  }, [pins])

  return <>{pinsComponents}</>
}
